import { useNavigate, useParams } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import {
  useEmpresaDelete,
  useEmpresaDetalhe,
} from '../../hooks/useEmpresas'
import styles from '../_shared/CrudPage.module.css'
import RTsEmpresaSection from './RTsEmpresaSection'
import AreasClienteSection from './AreasClienteSection'

const PAPEL_LABEL: Record<string, string> = {
  CLIENTE: 'Cliente (Contratante)',
  PLANEJADOR: 'Planejador (RT projeta o PMOC)',
  EXECUTOR: 'Executor (manutenção mecânica)',
  LABORATORIO: 'Laboratório (análise QAI)',
}

export default function EmpresaDetalhePage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: empresa, isLoading } = useEmpresaDetalhe(id)
  const del = useEmpresaDelete()

  async function handleDelete() {
    if (!empresa) return
    if (
      !confirm(
        `Inativar a empresa ${empresa.razao_social}?\nPMOCs e OS já criados continuam no histórico.`,
      )
    )
      return
    try {
      await del.mutateAsync(empresa.id)
      notifications.show({ color: 'green', message: 'Empresa inativada.' })
      navigate('/empresas')
    } catch {
      notifications.show({
        color: 'red',
        message: 'Não foi possível inativar a empresa.',
      })
    }
  }

  if (isLoading) {
    return <div className={styles.page}>Carregando...</div>
  }

  if (!empresa) {
    return <div className={styles.page}>Empresa não encontrada.</div>
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            className={styles.backBtn}
            type="button"
            onClick={() => navigate('/empresas')}
          >
            ← Voltar para Empresas
          </button>
          <h1 className={styles.title}>{empresa.razao_social}</h1>
          {empresa.is_self && (
            <span
              style={{
                marginLeft: 8,
                fontSize: '0.75rem',
                background: '#E3F2FD',
                color: '#1565C0',
                padding: '2px 8px',
                borderRadius: 3,
              }}
            >
              Sua empresa
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={styles.btnPrimary}
            type="button"
            onClick={() => navigate(`/empresas/${empresa.id}/editar`)}
          >
            Editar
          </button>
          {!empresa.is_self && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={del.isPending}
              style={{
                padding: '0.5rem 1rem',
                background: 'white',
                color: '#C62828',
                border: '1px solid #C62828',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              {del.isPending ? 'Inativando...' : 'Inativar'}
            </button>
          )}
        </div>
      </header>

      <section
        style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: 8,
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Identificação</h2>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '0.5rem 1rem',
          }}
        >
          <dt style={{ color: '#666' }}>Razão Social:</dt>
          <dd style={{ margin: 0 }}>{empresa.razao_social}</dd>
          <dt style={{ color: '#666' }}>Nome Fantasia:</dt>
          <dd style={{ margin: 0 }}>{empresa.nome_fantasia || '—'}</dd>
          <dt style={{ color: '#666' }}>CNPJ:</dt>
          <dd style={{ margin: 0 }}>{empresa.cnpj || '—'}</dd>
          <dt style={{ color: '#666' }}>Inscr. Estadual:</dt>
          <dd style={{ margin: 0 }}>{empresa.inscricao_estadual || '—'}</dd>
        </dl>
      </section>

      <RTsEmpresaSection empresa={empresa} />

      {empresa.papeis.includes('CLIENTE') && <AreasClienteSection empresaId={empresa.id} />}

      <section
        style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: 8,
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Papéis</h2>
        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
          {empresa.papeis.length === 0 ? (
            <li style={{ color: '#999' }}>Nenhum papel atribuído.</li>
          ) : (
            empresa.papeis.map((p) => (
              <li key={p}>{PAPEL_LABEL[p] || p}</li>
            ))
          )}
        </ul>
      </section>

      <section
        style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: 8,
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Endereço</h2>
        <p style={{ margin: 0 }}>
          {empresa.endereco_logradouro}
          {empresa.endereco_numero ? `, ${empresa.endereco_numero}` : ''}
          {empresa.endereco_complemento ? ` — ${empresa.endereco_complemento}` : ''}
          <br />
          {empresa.endereco_bairro && `${empresa.endereco_bairro} — `}
          {empresa.endereco_cidade}/{empresa.endereco_uf}
          {empresa.endereco_cep && ` — CEP ${empresa.endereco_cep}`}
        </p>
      </section>

      <section
        style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: 8,
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Contato</h2>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '0.5rem 1rem',
          }}
        >
          <dt style={{ color: '#666' }}>Telefone:</dt>
          <dd style={{ margin: 0 }}>{empresa.telefone || '—'}</dd>
          <dt style={{ color: '#666' }}>Email:</dt>
          <dd style={{ margin: 0 }}>{empresa.email || '—'}</dd>
          <dt style={{ color: '#666' }}>Website:</dt>
          <dd style={{ margin: 0 }}>
            {empresa.website ? (
              <a href={empresa.website} target="_blank" rel="noreferrer">
                {empresa.website}
              </a>
            ) : (
              '—'
            )}
          </dd>
        </dl>
      </section>

      {(empresa.registro_pj_conselho || empresa.registro_pj_numero) && (
        <section
          style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: '1rem' }}>
            Registro PJ no Conselho
          </h2>
          <p style={{ margin: 0 }}>
            {empresa.registro_pj_conselho === 'OUTRO'
              ? empresa.registro_pj_conselho_outro
              : empresa.registro_pj_conselho}
            {empresa.registro_pj_uf && `-${empresa.registro_pj_uf}`}{' '}
            {empresa.registro_pj_numero}
          </p>
        </section>
      )}
    </div>
  )
}
